import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

// Notification service controller that wraps SweetAlert2
// Provides consistent notification and confirmation dialogs across the app
export default class extends Controller {
  static values = {
    locale: { type: String, default: "fr" }
  }

  connect() {
    // Configure SweetAlert2 with project theme
    this.configureSwal()
    
    // Make this controller globally accessible
    window.NotificationService = this
  }

  disconnect() {
    // Clean up global reference
    if (window.NotificationService === this) {
      window.NotificationService = null
    }
  }

  configureSwal() {
    // Set up default configuration matching the project's design
    Swal.mixin({
      customClass: {
        popup: "rounded-lg shadow-2xl border-0",
        header: "pb-2",
        title: "text-lg font-medium text-content",
        content: "text-content",
        confirmButton: "bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200",
        cancelButton: "bg-midgray hover:bg-midgray/90 text-content font-medium py-2 px-4 rounded-md transition-colors duration-200 mr-3",
        input: "mt-2 block w-full rounded-md border-contour shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-20",
        validationMessage: "bg-error-light text-error border-error rounded-md p-2 mt-2"
      },
      buttonsStyling: false,
      reverseButtons: true,
      focusConfirm: false,
      allowOutsideClick: true,
      allowEscapeKey: true
    })
  }

  // Get localized text
  getText(key, fallback) {
    const texts = {
      fr: {
        confirm: "Confirmer",
        cancel: "Annuler",
        ok: "OK",
        yes: "Oui",
        no: "Non",
        delete: "Supprimer",
        error: "Erreur",
        warning: "Attention",
        success: "Succès",
        info: "Information"
      },
      en: {
        confirm: "Confirm",
        cancel: "Cancel", 
        ok: "OK",
        yes: "Yes",
        no: "No",
        delete: "Delete",
        error: "Error",
        warning: "Warning",
        success: "Success",
        info: "Information"
      },
      de: {
        confirm: "Bestätigen",
        cancel: "Abbrechen",
        ok: "OK", 
        yes: "Ja",
        no: "Nein",
        delete: "Löschen",
        error: "Fehler",
        warning: "Warnung",
        success: "Erfolg",
        info: "Information"
      },
      es: {
        confirm: "Confirmar",
        cancel: "Cancelar",
        ok: "OK",
        yes: "Sí", 
        no: "No",
        delete: "Eliminar",
        error: "Error",
        warning: "Advertencia",
        success: "Éxito",
        info: "Información"
      },
      nl: {
        confirm: "Bevestigen",
        cancel: "Annuleren",
        ok: "OK",
        yes: "Ja",
        no: "Nee", 
        delete: "Verwijderen",
        error: "Fout",
        warning: "Waarschuwing",
        success: "Succes",
        info: "Informatie"
      }
    }
    
    return texts[this.localeValue]?.[key] || texts.fr[key] || fallback
  }

  // Success notification
  async success(title, text = null, options = {}) {
    return await Swal.fire({
      ...options,
      icon: "success",
      title: title,
      text: text,
      confirmButtonText: this.getText("ok", "OK"),
      timer: options.timer || 3000,
      timerProgressBar: true,
      showConfirmButton: !options.timer,
      ...this.setupSwalZIndex()
    })
  }

  // Error notification
  async error(title, text = null, options = {}) {
    return await Swal.fire({
      ...options,
      icon: "error",
      title: title,
      text: text,
      confirmButtonText: this.getText("ok", "OK"),
      ...this.setupSwalZIndex()
    })
  }

  // Warning notification
  async warning(title, text = null, options = {}) {
    return await Swal.fire({
      ...options,
      icon: "warning",
      title: title,
      text: text,
      confirmButtonText: this.getText("ok", "OK"),
      ...this.setupSwalZIndex()
    })
  }

  // Info notification
  async info(title, text = null, options = {}) {
    return await Swal.fire({
      ...options,
      icon: "info",
      title: title,
      text: text,
      confirmButtonText: this.getText("ok", "OK"),
      ...this.setupSwalZIndex()
    })
  }

  // Simple alert replacement
  async alert(message, options = {}) {
    return await Swal.fire({
      ...options,
      title: message,
      confirmButtonText: this.getText("ok", "OK"),
      ...this.setupSwalZIndex()
    })
  }

  // Confirmation dialog
  async confirm(title, text = null, options = {}) {
    const zIndexCallbacks = this.setupSwalZIndex()
    const originalDidOpen = options.didOpen
    const originalDidClose = options.didClose

    const result = await Swal.fire({
      ...options,
      icon: "question",
      title: title,
      text: text,
      showCancelButton: true,
      confirmButtonText: options.confirmText || this.getText("confirm", "Confirmer"),
      cancelButtonText: options.cancelText || this.getText("cancel", "Annuler"),
      didOpen: (popup) => {
        zIndexCallbacks.didOpen(popup)
        if (originalDidOpen) originalDidOpen(popup)
      },
      didClose: () => {
        zIndexCallbacks.didClose()
        if (originalDidClose) originalDidClose()
      }
    })

    return result.isConfirmed
  }

  // Dangerous confirmation (for delete actions)
  async confirmDelete(title, text = null, options = {}) {
    const baseConfig = this.getSwalConfig()
    const zIndexCallbacks = this.setupSwalZIndex()
    const originalDidOpen = options.didOpen
    const originalDidClose = options.didClose

    // Check if we need input confirmation (for subscription cancellation)
    const needsInputConfirmation = title && title.includes("ANNULER") && title.includes("Tapez")

    if (needsInputConfirmation) {
      // Extract the confirmation word from the message
      const confirmWord = "ANNULER"
      const result = await Swal.fire({
        ...baseConfig,
        ...options,
        icon: "warning",
        title: "⚠️ ATTENTION ⚠️",
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>Voulez-vous vraiment annuler votre abonnement ?</strong></p>
            <p class="mb-2">Cette action est DÉFINITIVE et IRRÉVERSIBLE :</p>
            <ul class="list-disc list-inside mb-3">
              <li>Toutes vos données seront supprimées</li>
              <li>Votre boutique sera fermée</li>
              <li>Vous perdrez l'accès à tous vos produits</li>
            </ul>
            <p class="font-semibold">Tapez 'ANNULER' pour confirmer cette action dangereuse.</p>
          </div>
        `,
        input: "text",
        inputPlaceholder: "Tapez ANNULER pour confirmer",
        showCancelButton: true,
        confirmButtonText: "Confirmer l'annulation",
        cancelButtonText: "Garder mon abonnement",
        customClass: {
          ...baseConfig.customClass,
          confirmButton: "bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 mr-2"
        },
        inputValidator: (value) => {
          if (!value || value.toUpperCase() !== confirmWord) {
            return "Vous devez taper 'ANNULER' pour confirmer"
          }
        },
        didOpen: (popup) => {
          zIndexCallbacks.didOpen(popup)
          if (originalDidOpen) originalDidOpen(popup)
        },
        didClose: () => {
          zIndexCallbacks.didClose()
          if (originalDidClose) originalDidClose()
        }
      })

      return result.isConfirmed
    } else {
      // Regular delete confirmation
      const result = await Swal.fire({
        ...baseConfig,
        ...options,
        icon: "warning",
        title: title,
        text: text,
        showCancelButton: true,
        confirmButtonText: options.confirmText || this.getText("delete", "Supprimer"),
        cancelButtonText: options.cancelText || this.getText("cancel", "Annuler"),
        customClass: {
          ...baseConfig.customClass,
          confirmButton: "bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 mr-2"
        },
        didOpen: (popup) => {
          zIndexCallbacks.didOpen(popup)
          if (originalDidOpen) originalDidOpen(popup)
        },
        didClose: () => {
          zIndexCallbacks.didClose()
          if (originalDidClose) originalDidClose()
        }
      })

      return result.isConfirmed
    }
  }

  // Input prompt
  async prompt(title, options = {}) {
    const result = await Swal.fire({
      ...options,
      title: title,
      input: options.inputType || "text",
      inputPlaceholder: options.placeholder || "",
      inputValue: options.defaultValue || "",
      showCancelButton: true,
      confirmButtonText: options.confirmText || this.getText("confirm", "Confirmer"),
      cancelButtonText: options.cancelText || this.getText("cancel", "Annuler"),
      inputValidator: options.validator,
      ...this.setupSwalZIndex()
    })

    return result.isConfirmed ? result.value : null
  }

  // Loading state
  showLoading(title = null, text = null) {
    const zIndexCallbacks = this.setupSwalZIndex()
    Swal.fire({
      ...zIndexCallbacks,
      title: title || "Chargement...",
      text: text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: (popup) => {
        // Call z-index setup first
        if (zIndexCallbacks.didOpen) {
          zIndexCallbacks.didOpen(popup)
        }
        // Then show loading
        Swal.showLoading()
      }
    })
  }

  // Close any open modal
  close() {
    Swal.close()
  }

  // Get base Swal configuration with custom classes
  getSwalConfig() {
    return {
      customClass: {
        popup: "rounded-lg shadow-xl border-0",
        confirmButton: "bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 mr-2",
        cancelButton: "bg-midgray hover:bg-midgray/90 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200",
        denyButton: "bg-secondary hover:bg-secondary/90 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200",
        input: "px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
      },
      buttonsStyling: false,
      reverseButtons: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      heightAuto: false,
      backdrop: true
    }
  }

  // Helper to setup z-index and pointer events when SweetAlert opens
  setupSwalZIndex() {
    return {
      didOpen: (popup) => {
        // Set high z-index on SweetAlert container and popup
        const container = document.querySelector('.swal2-container')
        if (container) {
          container.style.setProperty('z-index', '999999', 'important')
        }

        if (popup) {
          popup.style.setProperty('z-index', '999999', 'important')
        }

        // Set z-index on backdrop
        const backdrop = document.querySelector('.swal2-backdrop-show')
        if (backdrop) {
          backdrop.style.setProperty('z-index', '999998', 'important')
        }

        // Disable pointer events on Turbo modal backdrop so SweetAlert is clickable
        const turboModal = document.querySelector('turbo-frame#modal')
        if (turboModal) {
          const turboBackdrop = turboModal.querySelector('[data-turbo-modal-target="modal"]')
          if (turboBackdrop) {
            turboBackdrop.style.setProperty('pointer-events', 'none', 'important')
            turboBackdrop.dataset.originalPointerEvents = turboBackdrop.style.pointerEvents || 'auto'
          }
        }
      },
      didClose: () => {
        // Restore pointer events on Turbo modal backdrop
        const turboModal = document.querySelector('turbo-frame#modal')
        if (turboModal) {
          const turboBackdrop = turboModal.querySelector('[data-turbo-modal-target="modal"]')
          if (turboBackdrop && turboBackdrop.dataset.originalPointerEvents) {
            turboBackdrop.style.pointerEvents = turboBackdrop.dataset.originalPointerEvents
            delete turboBackdrop.dataset.originalPointerEvents
          }
        }
      }
    }
  }

  // Toast notifications (non-blocking)
  toast(message, type = "info", options = {}) {
    const zIndexCallbacks = this.setupSwalZIndex()
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: options.timer || 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        // Apply z-index setup first
        if (zIndexCallbacks.didOpen) {
          zIndexCallbacks.didOpen(toast)
        }
        // Then add toast-specific listeners
        toast.addEventListener("mouseenter", Swal.stopTimer)
        toast.addEventListener("mouseleave", Swal.resumeTimer)
      },
      didClose: zIndexCallbacks.didClose,
      customClass: {
        popup: "rounded-lg shadow-lg border-0 text-sm"
      }
    })

    const iconMap = {
      success: "success",
      error: "error",
      warning: "warning",
      info: "info"
    }

    return Toast.fire({
      icon: iconMap[type] || "info",
      title: message,
      ...options
    })
  }

  // Utility methods for backward compatibility
  static async alert(message, options = {}) {
    return await window.NotificationService?.alert(message, options) || 
           Promise.resolve(window.alert(message))
  }

  static async confirm(title, text = null, options = {}) {
    return await window.NotificationService?.confirm(title, text, options) ||
           Promise.resolve(window.confirm(title + (text ? "\n" + text : "")))
  }

  static async confirmDelete(title, text = null, options = {}) {
    return await window.NotificationService?.confirmDelete(title, text, options) ||
           Promise.resolve(window.confirm(title + (text ? "\n" + text : "")))
  }

  static success(message, text = null, options = {}) {
    return window.NotificationService?.success(message, text, options) ||
           window.alert(message)
  }

  static error(message, text = null, options = {}) {
    return window.NotificationService?.error(message, text, options) ||
           window.alert(message)
  }

  static toast(message, type = "info", options = {}) {
    return window.NotificationService?.toast(message, type, options)
  }
}